import { format } from 'date-fns';
import db_config from '../configs/db_config';
import { db } from '../libs/firebase';
import { getCurrentJstDate, getJstDateKey, parseJstDateTime } from './jstDate';

export interface GameDayData {
  date: string;
  starttime_hh: number;
  starttime_mm: number;
  delivery_hh: number;
  delivery_mm: number;
  since_amount?: number;
  since_type?: string;
  until_amount: number;
  until_type: string;
  preorder?: boolean;
}

export interface PreOrderRules {
  time_slot?: {
    time: number;
    type: string;
  };
  order_deliberable?: {
    since?: { time: number; type: string };
    until?: { time: number; type: string };
  };
}

interface TimeSlotOption {
  value: string;
  label: string;
}

const convertTypeToMinutes = (amount: number, type: string): number => {
  const lowerType = type.toLowerCase();
  if (lowerType === 'minute' || lowerType === 'minutes') {
    return amount;
  } else if (lowerType === 'hour' || lowerType === 'hours') {
    return amount * 60;
  } else if (lowerType === 'day' || lowerType === 'days') {
    return amount * 60 * 24;
  }
  return amount;
};

const formatTimeSlot = (startTime: Date, endTime: Date): string => {
  const startStr = format(startTime, 'HH:mm');
  const endStr = format(endTime, 'HH:mm');
  return `${startStr}-${endStr}`;
};

const getGameDayDeliveryWindow = (gameDayData: GameDayData) => {
  const deliveryTime = parseJstDateTime(gameDayData.date, gameDayData.delivery_hh, gameDayData.delivery_mm);
  const gameStartTime = parseJstDateTime(gameDayData.date, gameDayData.starttime_hh, gameDayData.starttime_mm);

  const untilMinutes = convertTypeToMinutes(gameDayData.until_amount, gameDayData.until_type);

  return {
    startTime: deliveryTime,
    endTime: new Date(gameStartTime.getTime() + untilMinutes * 60 * 1000),
  };
};

export const getPreOrderDeliveryWindow = (gameDayData: GameDayData, restaurantPreOrderRules: PreOrderRules[] = []) => {
  const gameStartTime = parseJstDateTime(gameDayData.date, gameDayData.starttime_hh, gameDayData.starttime_mm);

  const rules = restaurantPreOrderRules.length ? restaurantPreOrderRules : [{} as PreOrderRules];

  return rules.reduce(
    (deliveryWindow, rule) => {
      const deliverableRule = rule.order_deliberable;
      const untilMinutes = convertTypeToMinutes(deliverableRule?.until?.time ?? 2, deliverableRule?.until?.type ?? 'Hours');
      const endTime = new Date(gameStartTime.getTime() + untilMinutes * 60 * 1000);

      return {
        startTime: gameStartTime,
        endTime: endTime > deliveryWindow.endTime ? endTime : deliveryWindow.endTime,
      };
    },
    {
      startTime: gameStartTime,
      endTime: gameStartTime,
    }
  );
};

const parseTimeSlotValue = (deliveryRequest: string, gameDate: string) => {
  const [startValue, endValue] = deliveryRequest.split('-');
  if (!startValue || !endValue) return null;

  const [startHours, startMinutes] = startValue.split(':').map(Number);
  const [endHours, endMinutes] = endValue.split(':').map(Number);
  if ([startHours, startMinutes, endHours, endMinutes].some((value) => Number.isNaN(value))) return null;

  const startTime = parseJstDateTime(gameDate, startHours, startMinutes);
  const endTime = parseJstDateTime(gameDate, endHours, endMinutes);

  return { startTime, endTime };
};

export const isDeliveryRequestInPreOrderWindow = (gameDayData: GameDayData, deliveryRequest: string | undefined, preOrderRules?: PreOrderRules) => {
  if (!deliveryRequest || deliveryRequest === 'WHEN_ITS_READY') return true;

  const selectedSlot = parseTimeSlotValue(deliveryRequest, gameDayData.date);
  if (!selectedSlot) return true;

  const { startTime, endTime } = getPreOrderDeliveryWindow(gameDayData, preOrderRules ? [preOrderRules] : []);
  return selectedSlot.startTime >= startTime && selectedSlot.endTime <= endTime;
};

export const fetchGameDayData = async (stadiumId: string, orderDate: string): Promise<GameDayData | null> => {
  try {
    const dateStr = getJstDateKey(orderDate);

    const gameDayRef = db.collection(db_config.DB_TABLE_STADIUMS_GAMEDAYS).doc(stadiumId).collection('gamedays');

    const snapshot = await gameDayRef.doc(dateStr).get();

    if (!snapshot.exists) {
      console.warn(`No game day data found for date: ${dateStr}`);
      return null;
    }

    const data = snapshot.data() as GameDayData;

    return data;
  } catch (error) {
    console.error('Error fetching game day data:', error);
    return null;
  }
};

export const generateTimeSlots = (
  gameDayData: GameDayData,
  preOrderRules?: PreOrderRules,
  isPreOrder: boolean = true,
  restaurantPreOrderRules: PreOrderRules[] = []
): TimeSlotOption[] => {
  try {
    let timeSlotMinutes = 30;
    if (isPreOrder && preOrderRules?.time_slot) {
      timeSlotMinutes = convertTypeToMinutes(preOrderRules.time_slot.time, preOrderRules.time_slot.type);
    }

    const { startTime, endTime } = isPreOrder
      ? getPreOrderDeliveryWindow(gameDayData, restaurantPreOrderRules)
      : getGameDayDeliveryWindow(gameDayData);

    if (startTime >= endTime) {
      console.warn('Delivery time is after or equal to end time. No time slots available.');
      return [];
    }

    const timeSlots: TimeSlotOption[] = [];
    let currentTime = new Date(startTime);
    const now = getCurrentJstDate();

    while (currentTime < endTime) {
      const slotEndTime = new Date(currentTime.getTime() + timeSlotMinutes * 60 * 1000);

      if (slotEndTime <= endTime) {
        const shouldIncludeSlot = isPreOrder || currentTime > now;
        
        if (shouldIncludeSlot) {
          const timeSlotLabel = formatTimeSlot(currentTime, slotEndTime);
          timeSlots.push({
            value: timeSlotLabel,
            label: timeSlotLabel,
          });
        }
      }

      currentTime = slotEndTime;
    }

    return timeSlots;
  } catch (error) {
    console.error('Error generating time slots:', error);
    return [];
  }
};
